import { useEffect, useRef, useState } from 'react';
import { LoadingImage } from '@/components/LoadingImage';

export function GenerationCandidate({
  imageUrl,
  imageUrls,
  videoUrl,
  caption,
  note,
  stale,
  onDecide,
  media = true,
}: {
  imageUrl: string | null;
  imageUrls?: string[];
  videoUrl?: string | null;
  caption?: string | null;
  note: string;
  stale: boolean;
  onDecide: (acceptance: 'accepted' | 'rejected') => Promise<void>;
  /** Off when the stage already shows this version: the picture is shown once. */
  media?: boolean;
}) {
  const images = imageUrls?.length ? imageUrls : imageUrl ? [imageUrl] : [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A version that lands below the fold is brought into view once, when it arrives.
  const box = useRef<HTMLElement>(null);
  useEffect(() => {
    box.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, []);
  const decide = async (value: 'accepted' | 'rejected') => {
    setBusy(true);
    setError(null);
    try {
      await onDecide(value);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your choice.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      ref={box}
      aria-label="Review generated version"
      style={{ padding: 16, border: '1px solid #ddd', borderRadius: 12, marginBottom: 16 }}
    >
      <h3>Review this version</h3>
      <p className="nx-note">{note || 'Your new draft'}</p>
      {!media ? null : videoUrl ? (
        <video
          controls
          src={videoUrl}
          poster={imageUrl ?? undefined}
          style={{ width: '100%', maxHeight: 360 }}
        />
      ) : (
        images.map((url, index) => (
          <a key={`${url}:${index}`} href={url} target="_blank" rel="noreferrer">
            <LoadingImage
              src={url}
              alt={`Generated card ${index + 1} awaiting your review`}
              imgStyle={{ width: '100%', maxHeight: 360, objectFit: 'contain' }}
            />
          </a>
        ))
      )}
      {caption && <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{caption}</p>}
      <p className="nx-note">
        {stale
          ? 'This was made before your newer edits. Those edits are preserved.'
          : media
            ? 'Review the result before using it. Your previous version is saved.'
            : 'Keep it to edit and share it, or discard it and start again.'}
      </p>
      {error && <p role="alert">{error}</p>}
      <div className="nx-chips">
        <button
          type="button"
          className="nx-chip"
          disabled={busy || stale}
          onClick={() => void decide('accepted')}
        >
          Keep this version
        </button>
        <button
          type="button"
          className="nx-chip"
          disabled={busy}
          onClick={() => void decide('rejected')}
        >
          Discard this version
        </button>
      </div>
    </section>
  );
}
